// Image-first social cards (Instagram, TikTok): which links get one, how tall
// the card is, and what its caption says. Pure: safe to import from cards.
import { parseSocialUrl, type SocialPlatform } from './socialLinks';
import { decodeHtmlEntities } from './htmlEntities';

export type SocialMediaType = 'image' | 'video' | 'carousel' | 'profile';

/** One re-hosted carousel slide. Video slides store their cover. */
export interface CarouselSlide {
  image: string;
  video?: boolean;
}

/** The fields of a link item's content the social card reads. */
export interface SocialCardContent {
  url?: string | null;
  tweet_url?: string | null;
  title?: string;
  description?: string;
  image?: string | null;
  imageAspectRatio?: number;
  platform?: string;
  mediaType?: SocialMediaType;
  authorHandle?: string;
  authorName?: string;
  authorAvatar?: string | null;
  authorFollowers?: number;
  recentImages?: string[];
  /** Carousels: every slide, the first being `image`. Empty when the server found none. */
  slides?: CarouselSlide[];
  previewSource?: string;
  previewPending?: boolean;
  previewUpdatedAt?: string;
}

export const IMAGE_FIRST_PLATFORMS: SocialPlatform[] = ['instagram', 'tiktok'];

/** Gradient bar along the top edge + one caption line under the media. */
export const SOCIAL_CARD_CHROME = 3 + 36;

function urlOf(content: SocialCardContent): string {
  return (typeof content.url === 'string' && content.url) || (typeof content.tweet_url === 'string' && content.tweet_url) || '';
}

/** Instagram or TikTok post, reel, video or profile: the links that get the image-first card. */
export function isImageFirstSocial(content: SocialCardContent | null | undefined): boolean {
  if (!content) return false;
  const info = parseSocialUrl(urlOf(content));
  return !!info && IMAGE_FIRST_PLATFORMS.includes(info.platform) && info.kind !== 'other';
}

/** Media type from the stored field, or from the URL on cards saved before it existed. */
export function socialMediaType(content: SocialCardContent): SocialMediaType {
  if (content.mediaType) return content.mediaType;
  const info = parseSocialUrl(urlOf(content));
  if (!info || !info.isPost) return 'profile';
  return info.kind === 'reel' || info.kind === 'video' || info.kind === 'short' ? 'video' : 'image';
}

/**
 * Instagram serves its own logo (static.cdninstagram.com/rsrc.php/...) to
 * logged-out scrapers. Old profile cards stored it as their image: never show it.
 */
export function isUsableSocialImage(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    // Relative paths resolve against a placeholder origin and count as usable
    const u = new URL(url, 'https://local.invalid');
    return u.hostname !== 'static.cdninstagram.com' && !u.pathname.includes('/rsrc.php');
  } catch {
    return false;
  }
}

/**
 * Carousel stack, after mymind: the current slide in front, the next one
 * peeking out behind it on the right (on the last slide the stack flips and
 * the previous one peeks out on the left), dots underneath. Fractions of the
 * media area: x and widths of its width, y and heights of its height.
 */
export const CAROUSEL_STACK = {
  padX: 0.05,
  padTop: 0.05,
  frontWidth: 0.78,
  frontHeight: 0.83,
  /** Peeking slide, relative to the front one. */
  peekScale: 0.85,
} as const;

/** Slides to show as a stack: two or more with usable images, else none. */
export function carouselSlides(content: SocialCardContent): CarouselSlide[] {
  if (socialMediaType(content) !== 'carousel' || !Array.isArray(content.slides)) return [];
  const slides = content.slides.filter((s) => s && isUsableSocialImage(s.image));
  return slides.length > 1 ? slides : [];
}

/** Width / height of the media area. Clamped so a panorama or a sliver still makes a sane card. */
export function socialMediaRatio(content: SocialCardContent): number {
  const type = socialMediaType(content);
  // Profiles: avatar, name and a row of recent posts need a 4:5 frame
  if (type === 'profile') return 4 / 5;
  const stored = isUsableSocialImage(content.image) ? content.imageAspectRatio : undefined;
  const ratio = stored && Number.isFinite(stored) ? stored : type === 'video' ? 9 / 16 : 4 / 5;
  const media = Math.min(1.91, Math.max(9 / 16, ratio));
  // A stack: wide enough that the front slide keeps the post's own ratio and the next one shows
  if (isUsableSocialImage(content.image) && carouselSlides(content).length > 0) {
    return (media * CAROUSEL_STACK.frontHeight) / CAROUSEL_STACK.frontWidth;
  }
  return media;
}

/** Card height for a card `width` wide: media at its natural ratio plus the chrome. */
export function socialCardHeight(content: SocialCardContent, width: number): number {
  return Math.round(width / socialMediaRatio(content)) + SOCIAL_CARD_CHROME;
}

/** Placeholder titles the URL parser writes ("Instagram post by @x"): not a caption. */
function isUrlTitle(title: string): boolean {
  return /^(Instagram|TikTok) (post|reel|video|link|story)( by @[\w.-]+)?$/i.test(title) || /^@[\w.-]+ on (Instagram|TikTok)$/i.test(title);
}

/** One line under the media: the caption, else a real title, else the handle. */
export function socialCaption(content: SocialCardContent): string {
  const description = decodeHtmlEntities(content.description ?? '')?.split('\n').map((l) => l.trim()).find(Boolean);
  if (description) return description;
  const title = decodeHtmlEntities(content.title ?? '')?.trim() ?? '';
  // Old cards carry Instagram's og:title: `NAME on Instagram: "caption…"`
  const quoted = title.match(/on Instagram: ["“](.+?)["”]?$/)?.[1];
  if (quoted) return quoted.trim();
  if (title && !isUrlTitle(title)) return title;
  const handle = content.authorHandle;
  return handle ? `@${handle}` : title || 'Instagram';
}

/** Saved before image re-hosting, or still waiting for the deep fetch: worth one background refresh. */
export function needsPreviewRefresh(content: SocialCardContent): 'free' | 'deep' | null {
  if (!isImageFirstSocial(content)) return null;
  if (content.previewPending) return 'deep';
  if (!content.previewUpdatedAt) return 'free';
  // Carousels saved before slides were stored: one free fetch picks them up
  if (socialMediaType(content) === 'carousel' && !Array.isArray(content.slides)) return 'free';
  return null;
}

// ── Top Creatives (scraped_creative items) ───────────────────────

/** Content of a scraped_creative item, as apify-scrape and classify-creative write it. */
export interface CreativeContent {
  platform?: string;
  account_handle?: string;
  account_name?: string | null;
  content_url?: string;
  /** Pulled from a pasted link: the URL the user pasted. */
  source_url?: string;
  thumbnail_url?: string | null;
  /** width / height of the media, when the scraper reported it. */
  media_aspect_ratio?: number | null;
  content_type?: string | null;
  hook_text?: string | null;
  caption?: string | null;
  views?: number;
  likes?: number;
  comments?: number;
  engagement_rate?: number;
  overall_creative_score?: number | null;
  /** Set once the thumbnail is in our Storage, by the server or a card refresh. */
  thumbnail_rehosted_at?: string;
}

/** Stats row under the caption on Top Creatives cards (28px row, pulled up 6px). */
export const CREATIVE_FOOTER = 22;

/** 'video' | 'reel' | 'clips' -> video; 'sidecar' | 'carousel' -> carousel; anything else -> image. */
export function creativeMediaType(contentType: string | null | undefined): SocialMediaType {
  const t = (contentType ?? '').toLowerCase();
  if (/video|reel|clip/.test(t)) return 'video';
  if (/sidecar|carousel|album/.test(t)) return 'carousel';
  return 'image';
}

/** Map a creative onto the social card's fields. */
export function creativeToSocialContent(c: CreativeContent): SocialCardContent {
  const handle = c.account_handle && c.account_handle !== 'unknown' ? c.account_handle : undefined;
  return {
    url: c.content_url || c.source_url || '',
    platform: c.platform,
    image: c.thumbnail_url ?? null,
    imageAspectRatio: c.media_aspect_ratio ?? undefined,
    mediaType: creativeMediaType(c.content_type),
    description: c.hook_text || c.caption || '',
    authorHandle: handle,
    authorName: c.account_name ?? undefined,
  };
}

/** Top Creatives card height at `width`: media at its ratio, caption line, stats row. */
export function creativeCardHeight(c: CreativeContent, width: number): number {
  return socialCardHeight(creativeToSocialContent(c), width) + CREATIVE_FOOTER;
}

/** A thumbnail still on the platform CDN (expires) rather than in our Storage. */
export function needsCreativeRehost(c: CreativeContent): boolean {
  const url = c.thumbnail_url;
  if (!url) return !!(c.content_url || c.source_url);
  return !url.includes('/storage/v1/object/public/');
}
